import React, { Fragment, useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { Link } from 'react-router-dom';
import generateRepairPDF from '../repairandreplacement/Bill';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import CopyrightIcon from '@mui/icons-material/Copyright';
import CircularProgress from '@mui/material/CircularProgress';
import '../shared.css';

const RepairAndReplacementEdit = () => {
    const { id } = useParams();
    const history = useHistory();
    const [errorMessage, setErrorMessage]           = useState('');
    const [errorMessageModel, setErrorMessageModel] = useState('');
    const [isLoading, setIsLoading]                 = useState(false);
    const [confirmRemove, setConfirmRemove]         = useState(null);

    const { isAuthenticated, companyId, branchId, userId, token, logout } = useAuth();

    const [selectedItemDescription, setSelectedItemDescription] = useState('0');
    const [selectedItemCategory, setSelectedItemCategory]       = useState('0');
    const [selectedSalesMan, setSelectedSalesMan]               = useState('');
    const [selectedCardType, setSelectedCardType]               = useState('');
    const [dropdownSalesManOptions, setDropdownSalesManOptions] = useState([]);
    const [dropdownCardTypeOptions, setDropdownCardTypeOptions] = useState([]);

    const [formData, setFormData]             = useState({});
    const [invoicedeatils, setInvoicedeatils] = useState([]);
    const [modalData, setModalData]           = useState({
        ReceiptType: '', ItemCategory: '', Amount: '',
        ReceiptTypeName: '', ItemCategoryName: '',
    });

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        fetchDropdownSalesManOptions();
        fetchDropdownCardTypeOptions();
    }, []);

    useEffect(() => {
        fetchData('RepairAndReplacementApi', `/${id}`, token)
            .then(response => {
                if (response.responseCode === '1111') {
                    setFormData({ ...response.response_data.RepairAndReplacement, UpdatedBy: userId });
                    setInvoicedeatils(response.response_data.RepairAndReplacementDetails);
                    setSelectedSalesMan(response.response_data.RepairAndReplacement.SalesManId);
                    setSelectedCardType(response.response_data.RepairAndReplacement.CardType);
                }
            })
            .catch(console.error);
    }, [id]);

    const fetchDropdownSalesManOptions = () => {
        fetchData('RepairAndReplacementApi', `/SalesMan/${companyId}/${branchId}`, token)
            .then(r => setDropdownSalesManOptions(r.response_data || []))
            .catch(console.error);
    };

    const fetchDropdownCardTypeOptions = () => {
        fetchData('RepairAndReplacementApi', `/CardType/${companyId}/${branchId}`, token)
            .then(r => setDropdownCardTypeOptions(r.response_data || []))
            .catch(console.error);
    };

    // ── Total calculation ──────────────────────────────────────────────────
    const totalCalculation = (amount, cardAmount, upiAmount) => {
        const total = parseFloat(amount)     || 0;
        const card  = parseFloat(cardAmount) || 0;
        const upi   = parseFloat(upiAmount)  || 0;
        const cash  = total - card - upi;
        setFormData(prev => ({
            ...prev,
            TotalAmount: total.toFixed(2),
            CashAmount:  cash.toFixed(2),
            CardAmount:  card.toFixed(2),
            UPIAmount:   upi.toFixed(2),
        }));
    };

    // ── Field change ───────────────────────────────────────────────────────
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'CardAmount') {
            const card = parseFloat(value) || 0;
            const amt  = invoicedeatils.reduce((s, i) => s + parseFloat(i.Amount), 0);
            const upi  = parseFloat(formData.UPIAmount) || 0;
            setFormData(prev => ({ ...prev, CardAmount: card.toFixed(2) }));
            totalCalculation(amt, card, upi);
        } else if (name === 'UPIAmount') {
            const upi  = parseFloat(value) || 0;
            const amt  = invoicedeatils.reduce((s, i) => s + parseFloat(i.Amount), 0);
            const card = parseFloat(formData.CardAmount) || 0;
            setFormData(prev => ({ ...prev, UPIAmount: upi.toFixed(2) }));
            totalCalculation(amt, card, upi);
        } else {
            setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    // ── Add item row ───────────────────────────────────────────────────────
    const saveModalData = () => {
        if (selectedItemDescription === '0') { setErrorMessageModel('Item Description is required'); return; }
        if (selectedItemCategory    === '0') { setErrorMessageModel('Item Category is required');    return; }
        if (!modalData.Amount || isNaN(modalData.Amount)) { setErrorMessageModel('Amount must be a valid number'); return; }

        setErrorMessageModel('');
        const updated = [modalData, ...invoicedeatils];
        setInvoicedeatils(updated);
        setModalData({ ReceiptType: '', ItemCategory: '', Amount: '', ReceiptTypeName: '', ItemCategoryName: '' });
        setSelectedItemDescription('0');
        setSelectedItemCategory('0');

        const amt  = updated.reduce((s, i) => s + parseFloat(i.Amount), 0);
        const card = parseFloat(formData.CardAmount) || 0;
        const upi  = parseFloat(formData.UPIAmount)  || 0;
        totalCalculation(amt, card, upi);
    };

    // ── Remove item row ────────────────────────────────────────────────────
    const removeRecord = (index) => setConfirmRemove(index);

    const confirmRemoveRecord = () => {
        const updated = [...invoicedeatils];
        updated.splice(confirmRemove, 1);
        setConfirmRemove(null);
        setInvoicedeatils(updated);
        const amt  = updated.reduce((s, i) => s + parseFloat(i.Amount), 0);
        const card = parseFloat(formData.CardAmount) || 0;
        const upi  = parseFloat(formData.UPIAmount)  || 0;
        totalCalculation(amt, card, upi);
    };

    // ── Submit ─────────────────────────────────────────────────────────────
    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage('');

        if (!formData.CustomerName)  { setErrorMessage('Customer Name is required!');    setIsLoading(false); return; }
        if (!formData.ContactNumber) { setErrorMessage('Contact Number is required!');   setIsLoading(false); return; }
        if (formData.CashAmount < 0) { setErrorMessage('Cash Amount cannot be negative!'); setIsLoading(false); return; }
        if (formData.CardAmount > 0 && !formData.CardNumber) { setErrorMessage('Card Number is required when Card Amount > 0!'); setIsLoading(false); return; }
        if (formData.CardAmount > 0 && !selectedCardType)    { setErrorMessage('Card Type is required when Card Amount > 0!');   setIsLoading(false); return; }
        if (formData.UPIAmount > 0  && !formData.UPINumber)  { setErrorMessage('UPI Number is required when UPI Amount > 0!');   setIsLoading(false); return; }
        if (!selectedSalesMan)       { setErrorMessage('Sales Man is required!');         setIsLoading(false); return; }
        if (!invoicedeatils.length)  { setErrorMessage('Item Details are required!');     setIsLoading(false); return; }

        const payload = {
            RepairAndReplacement: {
                ...formData,
                ModifiedBy: userId,
                BranchId: branchId,
                CompanyId: companyId,
                SalesManId: selectedSalesMan,
                CardType: selectedCardType,
            },
            RepairAndReplacementDetails: invoicedeatils,
        };

        updateData('RepairAndReplacementApi', `${id}`, payload, token)
            .then(response => {
                if (response.response === 'RepairAndReplacement Updated Successfully') {
                    handlePrint(response.kotId);
                }
                history.push('/RepairAndReplacementIndex');
            })
            .catch(console.error)
            .finally(() => setIsLoading(false));
    };

    const handlePrint = async (receiptId) => {
        try {
            const response = await fetchData('RepairAndReplacementApi', `/SalePrint/${receiptId}/${branchId}`, token);
            generateRepairPDF(response.response_data);
        } catch (error) {
            console.error('Error printing Invoice:', error);
        }
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    return (
        <Fragment>
            <div className="pg-container">
                {/* ── Header ── */}
                <div className="pg-header">
                    <h5 className="pg-title">Repair / Replacement Receipt — Edit</h5>
                    <div className="pg-header-actions">
                        <div className="pg-action-row">
                            <button className="pg-btn pg-btn-success" onClick={handleSubmit} disabled={isLoading}>
                                {isLoading ? <CircularProgress size={14} color="inherit" /> : <DoneIcon />} Save
                            </button>
                            <Link to="/RepairAndReplacementIndex" className="pg-btn pg-btn-danger">
                                <CloseIcon /> Cancel
                            </Link>
                        </div>
                    </div>
                </div>

                {errorMessage && <div className="pg-alert pg-alert-error">{errorMessage}</div>}

                <div className="pg-card">
                    {/* ── Row 1: Customer / Date / Receipt No ── */}
                    <div className="pg-hpanel">
                        <div className="pg-hcell" style={{ flex: '3 1 0' }}>
                            <span className="pg-hcell-label">Customer Name <span className="req">*</span></span>
                            <input className="pg-hcell-input" type="text" name="CustomerName"
                                placeholder="Customer" maxLength={100}
                                value={formData.CustomerName || ''} onChange={handleChange} />
                        </div>
                        <div className="pg-hcell" style={{ flex: '3 1 0' }}>
                            <span className="pg-hcell-label">Address</span>
                            <input className="pg-hcell-input" type="text" name="CustomerAddress"
                                placeholder="Address" maxLength={200}
                                value={formData.CustomerAddress || ''} onChange={handleChange} />
                        </div>
                        <div className="pg-hcell" style={{ flex: '1 1 0' }}>
                            <span className="pg-hcell-label">Phone No <span className="req">*</span></span>
                            <input className="pg-hcell-input" type="text" name="ContactNumber"
                                placeholder="Phone Number" maxLength={15}
                                value={formData.ContactNumber || ''} onChange={handleChange} />
                        </div>
                        <div className="pg-hcell" style={{ flex: '1 1 0' }}>
                            <span className="pg-hcell-label">Receipt Date <span className="req">*</span></span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="date" name="BillDate"
                                value={formatDateForInput(formData.BillDate)} readOnly />
                        </div>
                        <div className="pg-hcell" style={{ flex: '1 1 0' }}>
                            <span className="pg-hcell-label">Receipt No <span className="req">*</span></span>
                            <input className="pg-hcell-input pg-hcell-readonly" type="text" name="BillNumber"
                                placeholder="Receipt Number"
                                value={formData.BillNumber || ''} readOnly />
                        </div>
                    </div>

                    {/* ── Item add bar ── */}
                    <div className="pg-item-bar" style={{ marginTop: 12 }}>
                        <div className="pg-field" style={{ flex: '2 1 160px' }}>
                            <label className="pg-label">Item Description <span className="req">*</span></label>
                            <select className="pg-select" value={selectedItemDescription}
                                onChange={e => {
                                    setSelectedItemDescription(e.target.value);
                                    setModalData(prev => ({
                                        ...prev,
                                        ReceiptType: e.target.value,
                                        ReceiptTypeName: e.target.options[e.target.selectedIndex].text,
                                    }));
                                }}>
                                <option value="0">Select</option>
                                <option value="1">Repair Receipt</option>
                                <option value="2">Replacement Receipt</option>
                            </select>
                        </div>
                        <div className="pg-field" style={{ flex: '2 1 160px' }}>
                            <label className="pg-label">Item Category <span className="req">*</span></label>
                            <select className="pg-select" value={selectedItemCategory}
                                onChange={e => {
                                    setSelectedItemCategory(e.target.value);
                                    setModalData(prev => ({
                                        ...prev,
                                        ItemCategory: e.target.value,
                                        ItemCategoryName: e.target.options[e.target.selectedIndex].text,
                                    }));
                                }}>
                                <option value="0">Select</option>
                                <option value="1">Jewellery</option>
                                <option value="2">Watch</option>
                                <option value="3">Crystal</option>
                                <option value="4">Others</option>
                            </select>
                        </div>
                        <div className="pg-field xnarrow">
                            <label className="pg-label">Amount</label>
                            <input className="pg-input text-right" type="number" name="Amount"
                                placeholder="0.00"
                                value={modalData.Amount}
                                onChange={e => setModalData(prev => ({ ...prev, Amount: e.target.value }))} />
                        </div>
                        <div style={{ paddingBottom: 2 }}>
                            <button className="pg-btn pg-btn-primary" onClick={saveModalData}>
                                <AddIcon /> Add Item
                            </button>
                        </div>
                    </div>
                    {errorMessageModel && <div className="pg-alert pg-alert-error" style={{ marginTop: 4 }}>{errorMessageModel}</div>}

                    {/* ── Items table ── */}
                    <div className="pg-invoice-table-wrap">
                        <table className="pg-invoice-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Item Description</th>
                                    <th>Item Category</th>
                                    <th className="right">Amount</th>
                                    <th style={{ textAlign: 'center' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoicedeatils.length === 0
                                    ? <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8', padding: 14 }}>No items added yet.</td></tr>
                                    : invoicedeatils.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ textAlign: 'center', width: 40 }}>{index + 1}</td>
                                            <td>{item.ReceiptTypeName}</td>
                                            <td>{item.ItemCategoryName}</td>
                                            <td className="right">{parseFloat(item.Amount).toFixed(2)}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <IconButton size="small" onClick={() => removeRecord(index)}>
                                                    <DeleteIcon style={{ color: '#dc2626', fontSize: 17 }} />
                                                </IconButton>
                                            </td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </div>

                    {/* ── Totals Panel ── */}
                    <div className="pg-totals-panel">
                        {/* Left: Remarks + summary */}
                        <div className="pg-totals-left">
                            <div className="pg-totals-remarks">
                                <label className="pg-tpay-label">Remarks</label>
                                <textarea className="pg-remarks-textarea" name="Remarks" placeholder="Remarks"
                                    value={formData.Remarks || ''} onChange={handleChange} />
                            </div>
                            <div className="pg-totals-summary">
                                <div className="pg-tsum-row">
                                    <span className="pg-tsum-label">Total Amount</span>
                                    <span className="pg-tsum-value">{parseFloat(formData.TotalAmount || 0).toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="pg-totals-divider" />

                        {/* Right: Grand Total + Payments */}
                        <div className="pg-totals-right">
                            {/* Sales Man */}
                            <div className="pg-totals-remarks" style={{ marginBottom: 8 }}>
                                <label className="pg-tpay-label">Sales Man <span className="req">*</span></label>
                                <select className="pg-tpay-select" value={selectedSalesMan}
                                    onChange={e => {
                                        setSelectedSalesMan(e.target.value);
                                        setFormData(prev => ({ ...prev, SalesManId: e.target.value }));
                                    }}>
                                    <option value="">Select Sales Man</option>
                                    {dropdownSalesManOptions.map(c => <option key={c.Id} value={c.Id}>{c.Name}</option>)}
                                </select>
                            </div>

                            {/* Grand Total bar */}
                            <div className="pg-grand-total-bar">
                                <span className="pg-gt-label">Total Amount</span>
                                <span className="pg-gt-value">{parseFloat(formData.TotalAmount || 0).toFixed(2)}</span>
                            </div>

                            {/* Payment row */}
                            <div className="pg-totals-pay-row">
                                <div className="pg-tpay-cell">
                                    <label className="pg-tpay-label">Cash</label>
                                    <input className={`pg-tpay-input${parseFloat(formData.CashAmount) < 0 ? ' cash-negative' : ''}`}
                                        type="text" name="CashAmount" readOnly
                                        value={formData.CashAmount || '0.00'} />
                                </div>
                                <div className="pg-tpay-cell">
                                    <label className="pg-tpay-label">Card</label>
                                    <input className="pg-tpay-input" type="text" name="CardAmount"
                                        value={formData.CardAmount || ''} onChange={handleChange}
                                        placeholder="0.00" />
                                </div>
                                <div className="pg-tpay-cell">
                                    <label className="pg-tpay-label">UPI / RTGS / Other</label>
                                    <input className="pg-tpay-input" type="text" name="UPIAmount"
                                        value={formData.UPIAmount || ''} onChange={handleChange}
                                        placeholder="0.00" />
                                </div>
                            </div>

                            {/* Conditional extra fields */}
                            {(formData.CardAmount > 0 || formData.UPIAmount > 0) && (
                                <div className="pg-tpay-extra-row">
                                    {formData.CardAmount > 0 && (
                                        <div className="pg-tpay-cell">
                                            <label className="pg-tpay-label">Card Type <span className="req">*</span></label>
                                            <select className="pg-tpay-select" value={selectedCardType}
                                                onChange={e => {
                                                    setSelectedCardType(e.target.value);
                                                    setFormData(prev => ({ ...prev, CardType: e.target.value }));
                                                }}>
                                                <option value="">Select Card Type</option>
                                                {dropdownCardTypeOptions.map(c => <option key={c.Id} value={c.Id}>{c.Name}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    {formData.CardAmount > 0 && (
                                        <div className="pg-tpay-cell">
                                            <label className="pg-tpay-label">Card Number <span className="req">*</span></label>
                                            <input className="pg-tpay-input" type="text" name="CardNumber"
                                                placeholder="Card Number" maxLength={20}
                                                value={formData.CardNumber || ''} onChange={handleChange} />
                                        </div>
                                    )}
                                    {formData.UPIAmount > 0 && (
                                        <div className="pg-tpay-cell">
                                            <label className="pg-tpay-label">UPI / RTGS / Other No. <span className="req">*</span></label>
                                            <input className="pg-tpay-input" type="text" name="UPINumber"
                                                placeholder="UPI / RTGS / Other Number" maxLength={20}
                                                value={formData.UPINumber || ''} onChange={handleChange} />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Confirm Remove Modal ── */}
                {confirmRemove !== null && (
                    <div className="pg-modal-backdrop">
                        <div className="pg-modal-card">
                            <div className="pg-modal-icon-wrap">
                                <DeleteIcon className="pg-modal-icon" />
                            </div>
                            <p className="pg-modal-title">Remove Item?</p>
                            <p className="pg-modal-body">Are you sure you want to remove this item from the receipt?</p>
                            <div className="pg-modal-actions">
                                <button className="pg-modal-btn pg-modal-btn-cancel" onClick={() => setConfirmRemove(null)}>Cancel</button>
                                <button className="pg-modal-btn pg-modal-btn-confirm" onClick={confirmRemoveRecord}>Remove</button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
            </div>
        </Fragment>
    );
};

export default RepairAndReplacementEdit;
